/**
 * widget 运行时配置：后端入口地址 + 允许的宿主来源白名单。
 *
 * 约束来源：CONTRACT.md 第 60 行（API 28080 / gateway 28080，本地入口不写死远端）、
 * 第四节 iframe 接入要求。
 *
 * 安全口径（每条都有对应测试）：
 * - 宿主来源接受精确 origin，或显式单独配置 '*'；拒绝路径、子域通配和混用。
 * - 通配模式只有首次 widget.ready 使用 '*'，其余出站消息绑定实际父窗口来源。
 * - 这里只解析配置，不发请求、不读任何密钥：源 token 由宿主通过 postMessage 提供。
 */

import { normalizeOrigin, resolveEndpoints } from '@support/platform';

import {
  DEFAULT_API_BASE_URL,
  DEFAULT_HOST_ORIGINS,
  DEFAULT_WS_BASE_URL,
  parseHostOrigins,
} from './frame-ancestors';

export { DEFAULT_HOST_ORIGINS, parseHostOrigins };

export interface WidgetConfig {
  /** API 基地址（不含 /v1/support 前缀的原始值，仅用于诊断展示）。 */
  apiBaseUrl: string;
  /** 宿主来源（已去重的精确 origin，或单独的 '*'）。 */
  hostOrigins: string[];
  /** 已含 /v1/support 前缀的 HTTP 基地址。 */
  httpBase: string;
  /** widget 自身版本，随 widget.ready 上报。 */
  widgetVersion: string;
  /** 已含 /v1/support 前缀的实时基地址（ws/wss）。 */
  wsBase: string;
}

/** 允许的环境变量形状（import.meta.env 与测试用的普通对象都满足）。 */
export type WidgetEnv = Readonly<Record<string, unknown>>;

const DEFAULT_WIDGET_VERSION = '1.0.0';

function readString(env: WidgetEnv, key: string): string | undefined {
  const value = env[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function resolveWidgetConfig(env: WidgetEnv): WidgetConfig {
  const rawApiBase = readString(env, 'VITE_SUPPORT_API_BASE') ?? DEFAULT_API_BASE_URL;
  const apiBaseUrl = normalizeOrigin(rawApiBase);
  if (apiBaseUrl === null) {
    throw new Error(`API 基地址必须是精确的 http(s) 地址: ${rawApiBase}`);
  }
  const rawWsBase = readString(env, 'VITE_SUPPORT_WS_BASE') ?? DEFAULT_WS_BASE_URL;
  // 实时地址允许 ws/wss：先按 http(s) 归一化再交给 resolveEndpoints 转协议
  const wsBaseUrl =
    normalizeOrigin(rawWsBase) ??
    (rawWsBase.startsWith('ws://') || rawWsBase.startsWith('wss://')
      ? rawWsBase
      : null);
  if (wsBaseUrl === null) {
    throw new Error(`实时基地址必须是精确的 http(s)/ws(s) 地址: ${rawWsBase}`);
  }

  const rawOrigins = readString(env, 'VITE_SUPPORT_HOST_ORIGINS');
  const hostOrigins =
    rawOrigins === undefined ? [...DEFAULT_HOST_ORIGINS] : parseHostOrigins(rawOrigins);

  const { httpBase, wsBase } = resolveEndpoints({ apiBaseUrl, wsBaseUrl });

  return {
    apiBaseUrl,
    hostOrigins,
    httpBase,
    widgetVersion: readString(env, 'VITE_SUPPORT_WIDGET_VERSION') ?? DEFAULT_WIDGET_VERSION,
    wsBase,
  };
}
